import React, {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { AiSparkleIcon } from '@/components/icons/AiSparkleIcon'
import {
    Alert,
    Button,
    Input,
    List,
    Radio,
    Select,
    Space,
    Tooltip,
    Typography,
    message,
} from 'antd'
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    DeleteOutlined,
    LoadingOutlined,
    PaperClipOutlined,
    UploadOutlined,
} from '@ant-design/icons'
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil'
import { ContentPath, hashContentPath } from 'common/src/ContentPath'
import {
    AgentContentJob,
    AgentContentJobMode,
    AgentContentJobStatus,
    SourceMaterialImportMode,
} from 'common/src/api/contentFrontendVsContentBackend/agentContent'
import { AnthropicModelName } from 'common/src/api/contentFrontendVsContentBackend/llm'
import {
    getAgentContentJob,
    listAgentContentJobsForSection,
    stageSourceMaterialFile,
    startAgentContentJob,
} from '@/api/cb/agentContent'
import { agentActivityAtom } from '@/atoms/agentActivity'
import {
    frontendDisplayedCourseSelector,
    isActiveCourseReadOnlySelector,
} from '@/atoms/auth'
import { useInvalidateSourceMaterials } from '@/atoms/sourceMaterial'
import {
    slimLeafNodeAtomFamily,
    slimLeafNodeInvalidationAtomFamily,
} from '@/atoms/slimLeafNode'
import { invalidate } from '@/atoms/types'
import { viewingStateAtom, ViewingStateType } from '@/atoms/viewingState'
import { getAgentModelLabel } from '@/components/GlobalAgentActivity/agentActivityLabels'
import { syncErrorMessage, syncSuccessMessage } from '@/utils/syncMessages'

type UploadStatus = 'queued' | 'uploading' | 'done' | 'error'
interface StagedFile {
    file: File
    status: UploadStatus
    error?: string
}

const ALLOWED_EXTENSIONS = new Set(['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp'])
const MODELS = [
    AnthropicModelName['claude-sonnet-5-5'],
    AnthropicModelName['claude-opus-5-5'],
    AnthropicModelName['claude-fable-5'],
]
const isActive = (status: AgentContentJobStatus): boolean =>
    status === AgentContentJobStatus.queued ||
    status === AgentContentJobStatus.waitingForLock ||
    status === AgentContentJobStatus.running
const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
const formatRelativeTime = (timestamp: string): string => {
    const minutes = Math.max(
        0,
        Math.round((Date.now() - new Date(timestamp).getTime()) / 60_000)
    )
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.round(minutes / 60)
    return hours < 24 ? `${hours}h ago` : `${Math.round(hours / 24)}d ago`
}

interface Props {
    contentPath: ContentPath
    hashedContentPath: string
    sourceMaterialIDs: string[]
    hasUnsavedMaterialEdits: boolean
}

export const SourceMaterialImportAgentSurface: React.FC<Props> = (
    props
): ReactElement => {
    const courseName = useRecoilValue(frontendDisplayedCourseSelector)
    const isReadOnly = useRecoilValue(isActiveCourseReadOnlySelector)
    const activity = useRecoilValue(agentActivityAtom)
    const resetSlimLeafNode = useResetRecoilState(
        slimLeafNodeAtomFamily(props.hashedContentPath)
    )
    const invalidateSlimLeafNode = useSetRecoilState(
        slimLeafNodeInvalidationAtomFamily(props.hashedContentPath)
    )
    const invalidateSourceMaterials = useInvalidateSourceMaterials(
        props.sourceMaterialIDs
    )
    const setViewingState = useSetRecoilState(viewingStateAtom)
    const inputRef = useRef<HTMLInputElement | null>(null)
    const [files, setFiles] = useState<StagedFile[]>([])
    const [stagedImportId, setStagedImportId] = useState<string>()
    const [isUploading, setIsUploading] = useState(false)
    const [urlInput, setUrlInput] = useState('')
    const [urls, setUrls] = useState<string[]>([])
    const [urlError, setUrlError] = useState<string>()
    const [importMode, setImportMode] = useState(
        SourceMaterialImportMode.append
    )
    const [modelName, setModelName] = useState(MODELS[0])
    const [instructorRequest, setInstructorRequest] = useState('')
    const [isStarting, setIsStarting] = useState(false)
    const [justStartedJobId, setJustStartedJobId] = useState<string | null>()
    const [trackedJobId, setTrackedJobId] = useState<string | null>(null)
    const [errorJobId, setErrorJobId] = useState<string | null>(null)
    const [history, setHistory] = useState<AgentContentJob[]>([])

    const openRun = useCallback(
        (jobId: string): void =>
            setViewingState((current) => ({
                ...current,
                viewingStateType: ViewingStateType.agentActivity,
                id: jobId,
            })),
        [setViewingState]
    )
    const refreshHistory = useCallback(async (): Promise<void> => {
        const response = await listAgentContentJobsForSection({
            courseName,
            contentPath: props.contentPath,
        })
        if (response.data.success) {
            setHistory(
                response.data.payload.jobs
                    .filter(
                        (job) =>
                            job.mode ===
                            AgentContentJobMode.sourceMaterialImport
                    )
                    .sort(
                        (a, b) =>
                            new Date(b.createdAt).getTime() -
                            new Date(a.createdAt).getTime()
                    )
            )
        }
    }, [courseName, props.contentPath])
    useEffect(() => void refreshHistory(), [refreshHistory])

    const activeJob = useMemo(
        () =>
            activity?.activeJobs.find(
                (job) =>
                    job.mode === AgentContentJobMode.sourceMaterialImport &&
                    job.courseName === courseName &&
                    hashContentPath(job.contentPath) ===
                        props.hashedContentPath &&
                    isActive(job.status)
            ) ?? null,
        [activity?.activeJobs, courseName, props.hashedContentPath]
    )
    useEffect(() => {
        if (!activeJob) return
        setTrackedJobId(activeJob.jobId)
        setJustStartedJobId(undefined)
        setErrorJobId(null)
    }, [activeJob])
    useEffect(() => {
        if (!activity || activeJob) return
        const jobId = trackedJobId ?? justStartedJobId
        if (!jobId) return
        let mounted = true
        void (async () => {
            const response = await getAgentContentJob({ jobId })
            if (!mounted || !response.data.success) return
            const job = response.data.payload.job
            if (isActive(job.status)) return
            setTrackedJobId(null)
            setJustStartedJobId(undefined)
            if (job.status === AgentContentJobStatus.succeeded) {
                setErrorJobId(null)
                invalidateSourceMaterials()
                resetSlimLeafNode()
                invalidateSlimLeafNode(invalidate)
                syncSuccessMessage('Source materials updated by agent.')
            } else {
                setErrorJobId(jobId)
            }
            void refreshHistory()
        })()
        return () => {
            mounted = false
        }
    }, [
        activeJob,
        activity,
        invalidateSlimLeafNode,
        invalidateSourceMaterials,
        justStartedJobId,
        refreshHistory,
        resetSlimLeafNode,
        trackedJobId,
    ])

    const addFiles = useCallback(
        async (addedFiles: File[]): Promise<void> => {
            if (
                isReadOnly ||
                activeJob ||
                justStartedJobId !== undefined ||
                isUploading
            )
                return
            const accepted = addedFiles.filter((file) =>
                ALLOWED_EXTENSIONS.has(
                    file.name.split('.').pop()?.toLowerCase() ?? ''
                )
            )
            if (accepted.length !== addedFiles.length) {
                syncErrorMessage(
                    'Only PDF, PNG, JPG, JPEG, GIF, and WebP files are supported.'
                )
            }
            if (!accepted.length) return
            const startIndex = files.length
            setFiles((current) => [
                ...current,
                ...accepted.map((file) => ({
                    file,
                    status: 'queued' as const,
                })),
            ])
            setIsUploading(true)
            let currentStagedId = stagedImportId
            for (let offset = 0; offset < accepted.length; offset++) {
                const index = startIndex + offset
                setFiles((current) =>
                    current.map((item, itemIndex) =>
                        itemIndex === index
                            ? { ...item, status: 'uploading' }
                            : item
                    )
                )
                const response = await stageSourceMaterialFile(
                    courseName,
                    accepted[offset],
                    currentStagedId
                )
                if (response.data.success) {
                    currentStagedId = response.data.payload.stagedImportId
                    setStagedImportId(currentStagedId)
                    setFiles((current) =>
                        current.map((item, itemIndex) =>
                            itemIndex === index
                                ? { ...item, status: 'done' }
                                : item
                        )
                    )
                } else {
                    setFiles((current) =>
                        current.map((item, itemIndex) =>
                            itemIndex === index
                                ? {
                                      ...item,
                                      status: 'error',
                                      error: response.data.error,
                                  }
                                : item
                        )
                    )
                }
            }
            setIsUploading(false)
        },
        [
            activeJob,
            courseName,
            files.length,
            isReadOnly,
            isUploading,
            justStartedJobId,
            stagedImportId,
        ]
    )
    const addUrl = (): void => {
        try {
            const parsed = new URL(urlInput.trim())
            if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:')
                throw new Error()
            if (!urls.includes(parsed.toString()))
                setUrls((current) => [...current, parsed.toString()])
            setUrlInput('')
            setUrlError(undefined)
        } catch {
            setUrlError('Enter a valid http or https URL.')
        }
    }
    const completedFileCount = files.filter(
        (item) => item.status === 'done'
    ).length
    const displayedJobId = activeJob?.jobId ?? justStartedJobId
    const hasOptimisticJob = justStartedJobId !== undefined
    const disabled =
        isReadOnly || !!activeJob || hasOptimisticJob || isUploading
    // Live view of the in-flight job (status + progress tail). The activity
    // feed only carries status, so poll the job record while a run is shown.
    const [liveJob, setLiveJob] = useState<AgentContentJob | null>(null)
    useEffect(() => {
        if (!displayedJobId) {
            setLiveJob(null)
            return
        }
        let mounted = true
        const tick = async (): Promise<void> => {
            const response = await getAgentContentJob({
                jobId: displayedJobId,
            })
            if (mounted && response.data.success)
                setLiveJob(response.data.payload.job)
        }
        void tick()
        const interval = setInterval(() => void tick(), 5_000)
        return () => {
            mounted = false
            clearInterval(interval)
        }
    }, [displayedJobId])
    const startJob = async (): Promise<void> => {
        setIsStarting(true)
        setErrorJobId(null)
        setJustStartedJobId(null)
        try {
            const response = await startAgentContentJob({
                courseName,
                contentPath: props.contentPath,
                mode: AgentContentJobMode.sourceMaterialImport,
                modelName,
                instructorRequest: instructorRequest.trim() || undefined,
                sourceMaterialImport: {
                    stagedImportId: completedFileCount
                        ? stagedImportId
                        : undefined,
                    sourceUrls: urls.length ? urls : undefined,
                    importMode,
                },
            })
            if (!response.data.success) {
                setJustStartedJobId(undefined)
                syncErrorMessage(
                    `Failed to start source material import: ${response.data.error}`
                )
                return
            }
            const jobId = response.data.payload.jobId
            setTrackedJobId(jobId)
            setJustStartedJobId(jobId)
            message.success({
                content: (
                    <span>
                        {response.data.payload.waitingOnJobId
                            ? 'Run queued behind another job — it starts automatically when that run finishes. '
                            : 'Source material import started. '}
                        <Button
                            type="link"
                            size="small"
                            onClick={() => openRun(jobId)}
                        >
                            View run
                        </Button>
                    </span>
                ),
                duration: 8,
            })
        } finally {
            setIsStarting(false)
        }
    }
    const latestJob = history[0]
    const runningStatus =
        liveJob && isActive(liveJob.status)
            ? liveJob.status
            : activeJob?.status ?? AgentContentJobStatus.queued
    const runningStatusLabel =
        displayedJobId === null
            ? 'starting…'
            : runningStatus === AgentContentJobStatus.waitingForLock
              ? 'waiting for another run to finish'
              : runningStatus === AgentContentJobStatus.queued
                ? 'queued — waiting for an agent slot'
                : 'running'
    const latestProgressMessage =
        (liveJob && isActive(liveJob.status)
            ? liveJob.progressMessages[liveJob.progressMessages.length - 1]
            : undefined) ?? null

    return (
        <>
            <fieldset
                disabled={disabled}
                style={{ border: 0, margin: 0, padding: 0 }}
            >
                {latestJob && !displayedJobId && !errorJobId && (
                    <Alert
                        showIcon
                        type={
                            latestJob.status === AgentContentJobStatus.succeeded
                                ? 'success'
                                : 'warning'
                        }
                        style={{ marginBottom: 12 }}
                        message={
                            <Typography.Text>
                                {`Last agent run: ${latestJob.status} · ${getAgentModelLabel(latestJob.modelName)} · by ${latestJob.requestedByDisplayName ?? 'unknown'} · ${formatRelativeTime(latestJob.createdAt)} `}
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={() => openRun(latestJob.id)}
                                >
                                    View run
                                </Button>
                            </Typography.Text>
                        }
                    />
                )}
                <Typography.Text strong>Files</Typography.Text>
                <div
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={(event) => {
                        event.preventDefault()
                        void addFiles(Array.from(event.dataTransfer.files))
                    }}
                    onClick={() => inputRef.current?.click()}
                    style={{
                        border: '1px dashed #bfbfbf',
                        borderRadius: 6,
                        padding: 18,
                        textAlign: 'center',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        marginTop: 6,
                    }}
                >
                    <UploadOutlined /> Drop PDF or image files here, or click to
                    choose
                    <input
                        ref={inputRef}
                        type="file"
                        multiple
                        hidden
                        accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                        onChange={(event) => {
                            void addFiles(Array.from(event.target.files ?? []))
                            event.target.value = ''
                        }}
                    />
                </div>
                {!!files.length && (
                    <>
                        <List
                            size="small"
                            dataSource={files}
                            renderItem={(item) => (
                                <List.Item>
                                    <Space>
                                        {item.status === 'queued' && (
                                            <PaperClipOutlined />
                                        )}
                                        {item.status === 'uploading' && (
                                            <LoadingOutlined />
                                        )}
                                        {item.status === 'done' && (
                                            <CheckCircleOutlined
                                                style={{ color: '#52c41a' }}
                                            />
                                        )}
                                        {item.status === 'error' && (
                                            <Tooltip title={item.error}>
                                                <CloseCircleOutlined
                                                    style={{ color: '#ff4d4f' }}
                                                />
                                            </Tooltip>
                                        )}
                                        <span>
                                            {item.file.name} ·{' '}
                                            {formatSize(item.file.size)}
                                        </span>
                                    </Space>
                                </List.Item>
                            )}
                        />
                        <Button
                            size="small"
                            onClick={() => {
                                setFiles([])
                                setStagedImportId(undefined)
                            }}
                        >
                            Clear
                        </Button>
                    </>
                )}
                <Typography.Text
                    strong
                    style={{ display: 'block', marginTop: 16 }}
                >
                    URLs
                </Typography.Text>
                <Space.Compact style={{ width: '100%', marginTop: 6 }}>
                    <Input
                        value={urlInput}
                        status={urlError ? 'error' : undefined}
                        placeholder="https://…"
                        onChange={(event) => setUrlInput(event.target.value)}
                        onPressEnter={addUrl}
                    />
                    <Button onClick={addUrl}>Add</Button>
                </Space.Compact>
                {urlError && (
                    <Typography.Text type="danger">{urlError}</Typography.Text>
                )}
                <List
                    size="small"
                    dataSource={urls}
                    renderItem={(url) => (
                        <List.Item
                            actions={[
                                <Button
                                    key="remove"
                                    type="text"
                                    icon={<DeleteOutlined />}
                                    onClick={() =>
                                        setUrls((current) =>
                                            current.filter(
                                                (item) => item !== url
                                            )
                                        )
                                    }
                                />,
                            ]}
                        >
                            {url}
                        </List.Item>
                    )}
                />
                <Typography.Text
                    strong
                    style={{ display: 'block', marginTop: 12 }}
                >
                    Import mode
                </Typography.Text>
                <Radio.Group
                    value={importMode}
                    onChange={(event) => setImportMode(event.target.value)}
                >
                    <Space direction="vertical">
                        <Radio value={SourceMaterialImportMode.append}>
                            Append — keep existing materials and add new
                            entries.
                        </Radio>
                        <Radio value={SourceMaterialImportMode.replace}>
                            Replace — the agent produces the section's complete
                            new material set; existing entries it omits are
                            deleted.
                        </Radio>
                    </Space>
                </Radio.Group>
                {props.hasUnsavedMaterialEdits && (
                    <Typography.Text
                        type="warning"
                        style={{ display: 'block', marginTop: 8 }}
                    >
                        Unsaved manual edits to this section's materials may be
                        overwritten by a replace import.
                    </Typography.Text>
                )}
                <Typography.Text
                    strong
                    style={{ display: 'block', marginTop: 12 }}
                >
                    Model
                </Typography.Text>
                <Select
                    value={modelName}
                    onChange={setModelName}
                    options={MODELS.map((model) => ({
                        value: model,
                        label: getAgentModelLabel(model),
                    }))}
                    style={{ width: 220 }}
                />
                <Input.TextArea
                    value={instructorRequest}
                    onChange={(event) =>
                        setInstructorRequest(event.target.value)
                    }
                    placeholder="Optional guidance for the agent…"
                    autoSize={{ minRows: 2, maxRows: 5 }}
                    style={{ marginTop: 12 }}
                />
                <Tooltip
                    title={
                        isReadOnly
                            ? 'You have read-only access to this course'
                            : completedFileCount + urls.length === 0
                              ? 'Add at least one successfully staged file or URL'
                              : undefined
                    }
                >
                    <span>
                        <Button
                            type="primary"
                            loading={isStarting}
                            disabled={
                                disabled ||
                                completedFileCount + urls.length === 0
                            }
                            onClick={() => void startJob()}
                            style={{ marginTop: 12 }}
                        >
                            <AiSparkleIcon /> Start import
                        </Button>
                    </span>
                </Tooltip>
            </fieldset>
            {/* Status banners live OUTSIDE the disabled fieldset so View run
            stays clickable while a job is in flight */}
            {displayedJobId !== undefined && (
                <Alert
                    showIcon
                    icon={
                        runningStatus ===
                        AgentContentJobStatus.waitingForLock ? undefined : (
                            <LoadingOutlined />
                        )
                    }
                    type="info"
                    style={{ marginTop: 12 }}
                    message={`Source material import · ${getAgentModelLabel(liveJob?.modelName ?? modelName)} · ${runningStatusLabel}`}
                    description={
                        <span>
                            {latestProgressMessage && (
                                <Typography.Text type="secondary">
                                    {latestProgressMessage}{' '}
                                </Typography.Text>
                            )}
                            {displayedJobId && (
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={() => openRun(displayedJobId)}
                                >
                                    View run
                                </Button>
                            )}
                        </span>
                    }
                />
            )}
            {errorJobId && (
                <Alert
                    showIcon
                    type="error"
                    style={{ marginTop: 12 }}
                    message="Source material import did not complete."
                    description={
                        <Button
                            type="link"
                            size="small"
                            onClick={() => openRun(errorJobId)}
                        >
                            View run
                        </Button>
                    }
                />
            )}
        </>
    )
}
